// NEEDS A RESPONSE AND ANSWERED-BY, OVER CONVERSATIONS (C2, C3)
//
// ⭐ PURE: a wait's conversation in, a verdict or a rendering out. No file, no
// clock. tools/client-slack-eval-answered.ts reads the set and the segments.
//
// A WAIT here is a client turn inside its conversation (E3's segment): the
// target and the client messages `turnOf` joins to it. Three things are
// decided for it, the fields of the labelling guide's §1:
//
//   - needs_response, judged at the moment of the turn;
//   - start, the turn's first message that needed a response;
//   - answered_by, the first YWS response to the turn (a message id, or
//     "<id>:<emoji>" for a YWS reaction), and resolution.
//
// CANDIDATE (a), THE RULE (`ruleAnswer`): the turn needs a response when any
// of its messages is more than an acknowledgement by the rules' words
// (`messageKind`, lib/client-slack-waits.ts, today's `needs`); it is answered
// by the first YWS message in the conversation after the turn. No @ is read
// anywhere here; the conversation alone says what belongs together.
// Its variants:
//   - `nextTurn: "stop"`: the answer must come before the client's next turn
//     in the conversation; when the client speaks again first, this turn is
//     `superseded` by it.
//     `nextTurn: "ignore"`: any later YWS message answers it.
//   - `reactions`: a YWS reaction on a message of the turn answers it when no
//     YWS message does (the rule can't tell an ack-kind turn from a reply-kind
//     one, which the guide's reaction rule needs).
//
// CANDIDATES (b) AND (c), A MODEL, read a wait's conversation with what came
// after. They differ only in what the model is told about people:
//   - (b) as the triage prompt sees a channel today: only
//     an author carries their side; a mentioned person and a reactor are a
//     name (`renderAsRead(…, false)`).
//   - (c) every mention and every reactor carries its side, and the
//     conversation's people are listed with theirs (`roster`).
//
// RE-READS (`readTimes`): phase 2 reads a wait at the half-hourly tick after
// it and could read it again at each tick its conversation gains a message. The tool measures how often such a re-read changes the verdict.
//
// PER CONVERSATION (E9): one call reads a whole conversation and answers for
// every client turn in it (`conversationTurns`), against E4's one call per
// turn. `withinHorizon` holds both to gold's horizon, and `readPlan` counts
// what each shape calls on the half-hourly tick: a turn re-read on every tick
// its conversation moves for 3 days, against the conversation re-read once on
// such a tick, answering every live turn.
//
// Erasable TypeScript: bare node runs the tool that imports it.

import { readable, stamp, type EvalMessage, type EvalPerson, type Side, type WaitLabel } from "@/lib/client-slack-eval";
import { messageKind } from "@/lib/client-slack-waits";
import { turnOf, type SegmentMessage } from "@/lib/client-slack-segments";

/** A message's side, as the guide's tags say it (an unknown person is a client). */
export function sideOf(m: EvalMessage, people: Readonly<Record<string, EvalPerson>>): Side {
  if (m.bot) return "bot";
  const p = m.user === null ? undefined : people[m.user];
  return p?.side ?? "client";
}

const secs = (m: EvalMessage): number => Number(m.ts);
const byTime = (a: EvalMessage, b: EvalMessage): number => secs(a) - secs(b) || Number(a.id.slice(1)) - Number(b.id.slice(1));

export interface RuleMode {
  nextTurn: "stop" | "ignore";
  reactions: boolean;
}

/** The fields a candidate answers about a wait. */
export type AnsweredFields = Pick<WaitLabel, "needs_response" | "start" | "answered_by" | "resolution">;

/**
 * Candidate (a): the rule, over the messages of the wait's conversation (any
 * order) and its turn. See the head of this file.
 */
export function ruleAnswer(conversation: readonly EvalMessage[], people: Readonly<Record<string, EvalPerson>>, turn: readonly string[], mode: RuleMode): AnsweredFields {
  const ms = conversation.slice().sort(byTime);
  const inTurn = new Set(turn);
  const turnMs = ms.filter((m) => inTurn.has(m.id));
  const needy = turnMs.filter((m) => messageKind(m.text) !== "ack");
  if (turnMs.length === 0 || needy.length === 0) return { needs_response: false, start: null, answered_by: null, resolution: "not_needed" };
  const start = needy[0].id;
  const last = turnMs[turnMs.length - 1];
  for (const m of ms) {
    if (byTime(m, last) <= 0 || inTurn.has(m.id)) continue;
    const side = sideOf(m, people);
    if (side === "yws") return { needs_response: true, start, answered_by: m.id, resolution: "answered" };
    if (side === "client" && mode.nextTurn === "stop") return { needs_response: true, start, answered_by: null, resolution: "superseded" };
  }
  if (mode.reactions)
    for (const m of turnMs.slice().reverse())
      for (const r of m.reactions)
        if (r.users.some((u) => people[u]?.side === "yws")) return { needs_response: true, start, answered_by: `${m.id}:${r.name}`, resolution: "acknowledged" };
  return { needs_response: true, start, answered_by: null, resolution: "open" };
}

// ---------------------------------------------------------------- what a model reads

const tag = (p: EvalPerson | undefined): string => (p === undefined ? "[client]" : p.side === "yws" ? "[YWS]" : p.side === "bot" ? "[bot]" : "[client]");

/**
 * One message as one line. With `roles`, exactly as the labelling guide shows
 * it (renderMessage): every mention and reactor with their side. Without, only
 * the author carries a side: a mentioned person and a reactor are a name.
 */
export function renderAsRead(m: EvalMessage, people: Readonly<Record<string, EvalPerson>>, roles: boolean): string {
  const p = m.user === null ? undefined : people[m.user];
  const who = m.bot ? `${p?.name ?? "A bot"} [bot]` : `${p?.name ?? "Unknown person"} ${tag(p)}`;
  const where = m.thread === null ? "" : m.thread === m.id ? ` (thread root, ${m.replies} repl${m.replies === 1 ? "y" : "ies"})` : ` (reply in thread ${m.thread})`;
  // Without roles, a mention becomes a bare name before `readable` could tag it.
  const text = roles ? m.text : m.text.replace(/<@(\w+)(?:\|[^>]*)?>/g, (_, u: string) => `@${people[u]?.name ?? "someone"}`);
  const files = m.files.map((f) => ` [file: ${f || "unnamed"}]`).join("");
  const reactor = (u: string) => (roles ? `${people[u]?.name ?? "someone"} ${tag(people[u])}` : (people[u]?.name ?? "someone"));
  const reacts = m.reactions.length ? ` {reactions: ${m.reactions.map((r) => `:${r.name}: by ${r.users.map(reactor).join(", ")}`).join("; ")}}` : "";
  return `${m.id} · ${stamp(m.ts)} · ${who}${where}: ${readable(text, people as Record<string, EvalPerson>)}${files}${reacts}`;
}

/** Everyone a conversation names (authors, mentioned people, reactors), with their side, in first appearance. */
export function roster(conversation: readonly EvalMessage[], people: Readonly<Record<string, EvalPerson>>): Array<{ name: string; side: Side }> {
  const seen = new Map<string, { name: string; side: Side }>();
  const add = (u: string | null) => {
    if (u === null || seen.has(u)) return;
    const p = people[u];
    seen.set(u, { name: p?.name ?? "someone", side: p?.side ?? "client" });
  };
  for (const m of conversation.slice().sort(byTime)) {
    add(m.user);
    for (const x of m.text.matchAll(/<@(\w+)(?:\|[^>]*)?>/g)) add(x[1]);
    for (const r of m.reactions) r.users.forEach(add);
  }
  return Array.from(seen.values());
}

// ---------------------------------------------------------------- re-reads

/** The half-hourly tick, in seconds. */
export const TICK_S = 1800;

/** The first tick strictly after `t`. */
const nextTick = (t: number, tick: number): number => (Math.floor(t / tick) + 1) * tick;

/**
 * When phase 2 would read a wait: at the first tick after its target, then
 * at every later tick its conversation gained a message (any side). Times in
 * seconds, ascending.
 */
export function readTimes(conversation: readonly EvalMessage[], target: string, tick = TICK_S): number[] {
  const t = conversation.find((m) => m.id === target);
  if (!t) return [];
  const first = nextTick(secs(t), tick);
  const out = new Set<number>([first]);
  for (const m of conversation) if (secs(m) >= first) out.add(nextTick(secs(m), tick));
  return Array.from(out).sort((a, b) => a - b);
}

/** A verdict as a re-read compares it: owed or not, answered by what, and how it ended. */
export const verdictKey = (v: AnsweredFields): string => `${v.needs_response}|${v.answered_by ?? "-"}|${v.resolution}`;

/** Resolved: nothing more is owed, so a policy of "re-read until resolved" stops here. */
export const resolved = (v: AnsweredFields): boolean => v.resolution !== "open";

export interface RereadStats {
  waits: number;
  reads: number;
  /** Reads after the first. */
  rereads: number;
  /** Re-reads whose verdict differs from the read before. */
  changed: number;
  /** Of those, changes once the verdict had first said resolved (what "stop once resolved" would miss). */
  changedAfterResolved: number;
  /** Waits whose verdict changed at least once after a resolved read. */
  waitsChangedAfterResolved: number;
  /** Re-reads a "stop once resolved" policy would still make. */
  rereadsUntilResolved: number;
  /** needs_response flipping on a re-read (it is judged at the turn: it shouldn't). */
  needsFlips: number;
}

/** What re-reading every wait at every tick its conversation moved would cost and change. */
export function rereadStats(sequences: ReadonlyArray<readonly AnsweredFields[]>): RereadStats {
  const s: RereadStats = { waits: 0, reads: 0, rereads: 0, changed: 0, changedAfterResolved: 0, waitsChangedAfterResolved: 0, rereadsUntilResolved: 0, needsFlips: 0 };
  for (const seq of sequences) {
    if (seq.length === 0) continue;
    s.waits++;
    s.reads += seq.length;
    s.rereads += seq.length - 1;
    let wasResolved = resolved(seq[0]);
    let after = false;
    for (let i = 1; i < seq.length; i++) {
      if (!wasResolved) s.rereadsUntilResolved++;
      if (verdictKey(seq[i]) !== verdictKey(seq[i - 1])) {
        s.changed++;
        if (wasResolved) {
          s.changedAfterResolved++;
          after = true;
        }
      }
      if (seq[i].needs_response !== seq[i - 1].needs_response) s.needsFlips++;
      wasResolved = wasResolved || resolved(seq[i]);
    }
    if (after) s.waitsChangedAfterResolved++;
  }
  return s;
}

// ---------------------------------------------------------------- per conversation (E9)

/**
 * Every client turn of a conversation, in time order: each client message not
 * yet in a turn takes `turnOf`'s turn (client messages with no YWS message
 * between and no gap over the turn gap; bots skipped). The turns partition the
 * conversation's client messages, so a wait's turn is exactly one of them.
 */
export function conversationTurns(messages: readonly SegmentMessage[], conversation: readonly string[]): string[][] {
  const inConv = new Set(conversation);
  const ms = messages.filter((m) => inConv.has(m.id)).sort((a, b) => Number(a.ts) - Number(b.ts) || Number(a.id.slice(1)) - Number(b.id.slice(1)));
  const taken = new Set<string>();
  const out: string[][] = [];
  for (const m of ms) {
    if (m.side !== "client" || taken.has(m.id)) continue;
    const t = turnOf(ms, conversation, m.id);
    t.forEach((id) => taken.add(id));
    out.push(t);
  }
  return out;
}

/**
 * A verdict held to what its reader was allowed to see: an answer outside
 * `shown` (past gold's 3-day horizon for this wait) reads `open`, as gold's
 * guide says a late answer does. Anything else is returned unchanged.
 */
export function withinHorizon(v: AnsweredFields, shown: ReadonlySet<string>): AnsweredFields {
  if (v.answered_by === null) return v;
  const id = /^(m\d+):/.exec(v.answered_by)?.[1] ?? v.answered_by;
  return shown.has(id) ? v : { ...v, answered_by: null, resolution: "open" };
}

export interface ReadPlan {
  /** One call per turn per tick it is read (E4's policy). */
  perTurn: number;
  /** One call per conversation per tick any of its turns is read. */
  perConversation: number;
  /** For each per-conversation call, how many turns it answers (the turns live at that tick). */
  turnsPerCall: number[];
}

/**
 * The calls each shape makes for one conversation on the tick. A turn is live
 * from its first message for `horizonS` (3 days): it is read at the first tick
 * after each message of the conversation that arrives while it is live (its
 * own messages included). Per turn, every live turn is one call at each such
 * tick; per conversation, a tick with any live turn is one call answering them
 * all. `messages` are the conversation's (any order), `turns` its turns.
 */
export function readPlan(messages: readonly SegmentMessage[], turns: ReadonlyArray<readonly string[]>, horizonS = 3 * 86_400, tick = TICK_S): ReadPlan {
  const ts = new Map(messages.map((m) => [m.id, Number(m.ts)]));
  const all = messages.map((m) => Number(m.ts));
  const live = new Map<number, number>();
  let perTurn = 0;
  for (const t of turns) {
    const a = Math.min(...t.map((id) => ts.get(id) ?? Infinity));
    if (!Number.isFinite(a)) continue;
    const ticks = new Set<number>();
    for (const x of all) if (x >= a && x <= a + horizonS) ticks.add(nextTick(x, tick));
    perTurn += ticks.size;
    for (const k of ticks) live.set(k, (live.get(k) ?? 0) + 1);
  }
  const keys = Array.from(live.keys()).sort((x, y) => x - y);
  return { perTurn, perConversation: keys.length, turnsPerCall: keys.map((k) => live.get(k) as number) };
}
